// Angular + external
import { Component, OnInit, AfterViewInit, ViewChild, HostListener } from "@angular/core";
import { Subscription } from "rxjs";
// BFX
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxDashboardMenuService } from "../bfx-common/services/bfx-dashboard-menu.service";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../golden-layout/golden-layout-service";
import { GoldenLayoutAdditionMode } from "../golden-layout/golden-layout-addition-mode";
import { DashboardItemType } from "../bfx-common/models/dashboard-item-type.enum";
import { TranslateService } from "@ngx-translate/core";

import {
  BfxUserSettingsService,
  BfxUserSettingsComponentState,
  BfxUserSettingsCategoryIndex,
  BfxUserSettingsGeneralSubCategoryIndex,
} from "../bfx-user-settings/bfx-user-settings.service";
import { TieredMenu } from "@bfx/primeng/tieredmenu";
import { LogoutNotificationService } from "app/bfx-auth/logout-notification.service";

import {
  SystemConfigPromiseService,
  Roles,
  SysFeatures,
  NotificationService,
  ActiveUserProfilePromiseService,
  ActiveUser,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxSaveProfileNotificationService } from "../bfx-common/services/bfx-save-profile-notification.service";

import { BfxPublicProfileService } from "../bfx-common/services/bfx-public-profile.service";
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";

@Component({
  selector: "bfx-user-menu",
  templateUrl: "./html/bfx-user-menu.html",
  standalone: false,
})
export class BfxUserMenuComponent implements OnInit, AfterViewInit {
  /***************************************/
  /* DATA */
  public config: any;
  private configurationSubscription: Subscription;
  public menuItems: Array<any>;
  public hasAdminPanicRole: boolean;
  private hasAdminPublicProfiles: boolean = true;

  public username: string;

  @ViewChild(TieredMenu) menu: TieredMenu;

  /**********************************/
  /* CONSTRUCTOR + LIFE-CYCLE HOOKS*/

  constructor(
    private _configService: BfxConfigService,
    private dashboardMenuService: BfxDashboardMenuService,
    private userSettingsService: BfxUserSettingsService,
    private translationService: TranslateService,
    private goldenLayoutService: GoldenLayoutService,
    private _logoutNotificationService: LogoutNotificationService,
    private _authService: BfxAuthService,
    public systemConfigService: SystemConfigPromiseService,
    private saveProfileNotificationService: BfxSaveProfileNotificationService,
    private publicProfileService: BfxPublicProfileService,
    private notifService: NotificationService,
    private activeUserService: ActiveUserProfilePromiseService,
  ) {
    this.subscribeToConfiguration();
  }

  ngOnInit() {
    // This is user code, used on login
    this.username = this._authService.getUsername();

    Promise.all([
      this.systemConfigService.getSystemConfig(SysFeatures.displayUserNames),
      this.activeUserService.getActiveUserData(),
    ])
      .then((value: [string, ActiveUser]) => {
        try {
          let showUserName: boolean =
            value.length > 0 &&
            (value[0] == "true" ||
              value[0] == "TRUE" ||
              value[0] == "1" ||
              value[0] == "Y" ||
              value[0] == "y");
          if (showUserName && value.length > 1 && value[1] != null && value[1].username) {
            this.username = value[1].username;
          } else if (value.length > 1 && value[1] != null && value[1].userCode) {
            this.username = value[1].userCode;
          }
        } catch (ex) {
          logger.out.error(
            `${this.constructor.name}: Failed reading config for show display name value: ${ex}`,
          );
        }
      })
      .catch((er) => {
        logger.out.error(
          `${this.constructor.name}: Failed to determine show display name value: ${er}`,
        );
      });
  }

  ngAfterViewInit() {
    this.hasSystemRole();
    this.initialiseHasAdminPublicProfiles();
  }

  ngOnDestroy() {
    this.configurationSubscription.unsubscribe();
  }

  /**********************************/
  /* INITIALIZATION */

  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config = config;
    });
  }

  /**********************************/
  /* PUBLIC Methods */

  /**
   * Works on desktop
   */
  // Fixes https://www.bcdfx.com/ijira/browse/OMS-29530
  @HostListener("window:resize", ["$event"])
  getWindowHeight(event?): void {
    if (this.menu != null) this.menu.hide();
  }

  public userSettingsWindowConfig(event: Event): void {
    let title: string = this.translationService.instant("user.settings.title");
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    let componentState: BfxUserSettingsComponentState =
      this.userSettingsService.getComponentState();

    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;
    config.minimumSize = { width: 818, height: 571 };
    config.sourceEvent = event;

    config.preferredSize = componentState.componentBounds;
    if (
      config.preferredSize &&
      config.preferredSize.height &&
      config.preferredSize.height < config.minimumSize.height
    ) {
      config.preferredSize.height = config.minimumSize.height;
    }
    if (
      config.preferredSize &&
      config.preferredSize.width &&
      config.preferredSize.width < config.minimumSize.width
    ) {
      config.preferredSize.width = config.minimumSize.width;
    }

    this.goldenLayoutService.launchFromDashboard(
      title,
      DashboardItemType.UserSettings,
      false,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  public onClickToggle($event): void {
    this.menuItems = this.constructMenu();
    this.menu.toggle($event);
  }

  public help(): void {
    let promiseArray = new Array<Promise<string>>();
    promiseArray.push(
      this.systemConfigService.getSystemConfig(SysFeatures.helpURL).catch((error) => {
        logger.out.error(`ERROR: BfxUserMenuComponent.help(): ${error}`);
        return null;
      }),
    );
    promiseArray.push(
      this.systemConfigService.getSystemConfig(SysFeatures.helpUser).catch((error) => {
        logger.out.error(`ERROR: BfxUserMenuComponent.help(): ${error}`);
        return null;
      }),
    );
    promiseArray.push(
      this.systemConfigService.getSystemConfig(SysFeatures.helpPass).catch((error) => {
        logger.out.error(`ERROR: BfxUserMenuComponent.help(): ${error}`);
        return null;
      }),
    );
    promiseArray.push(
      this.systemConfigService.getSystemConfig(SysFeatures.helpValidatorURL).catch((error) => {
        logger.out.error(`ERROR: BfxUserMenuComponent.help(): ${error}`);
        return null;
      }),
    );

    Promise.all(promiseArray).then((values: Array<string>) => {
      let url = values[0];
      let user = values[1];
      let pass = values[2];

      if (
        typeof url === "string" &&
        user.length > 0 &&
        typeof user === "string" &&
        url.length > 0 &&
        typeof pass === "string"
      ) {
        var helpForm = document.createElement("form");
        helpForm.setAttribute("method", "post");
        helpForm.setAttribute("action", url);
        helpForm.setAttribute("target", "help");
        // Username
        var userField = document.createElement("input");
        userField.setAttribute("type", "hidden");
        userField.setAttribute("id", "os_username");
        userField.setAttribute("name", "os_username");
        userField.setAttribute("value", user);
        // Password
        var passField = document.createElement("input");
        passField.setAttribute("type", "hidden");
        passField.setAttribute("id", "os_password");
        passField.setAttribute("name", "os_password");
        passField.setAttribute("value", pass);

        helpForm.appendChild(userField);
        helpForm.appendChild(passField);

        document.body.appendChild(helpForm);
        helpForm.submit();
        document.body.removeChild(helpForm);
      } else {
        logger.out.error(
          `ERROR: BfxUserMenuComponent.help(): Could not redirect to help due to invalid parameters`,
        );
      }
    });
  }

  public panicControls(event: Event): void {
    let componentState: BfxUserSettingsComponentState =
      this.userSettingsService.getComponentState();

    this.userSettingsWindowConfig(event);
    componentState.activeCategory = BfxUserSettingsCategoryIndex.Panic;
  }

  public showMenu(): void {
    this.dashboardMenuService.showHideMenu();
  }

  public saveUserProfile(): void {
    this.goldenLayoutService.saveProfileConfig().then(
      () => {
        this.saveProfileNotificationService.notifySuccessOnFooter();
      },
      (error) => {
        this.saveProfileNotificationService.notifyFailureWithPopup();
      },
    );
  }

  public saveUserProfileAsPublic(): void {
    this.publicProfileService.savePublicUserProfile();
  }

  public switchProfile(): void {
    this.publicProfileService.switchProfile();
  }

  public settings(event: Event): void {
    let componentState: BfxUserSettingsComponentState =
      this.userSettingsService.getComponentState();

    this.userSettingsWindowConfig(event);
    componentState.activeCategory = BfxUserSettingsCategoryIndex.General;
    componentState.activeSubCategory = BfxUserSettingsGeneralSubCategoryIndex.General;
  }

  public requestLogout(): void {
    this._logoutNotificationService.requestLogout().subscribe(
      (logoutAccepted) => {
        if (logoutAccepted) {
          this._authService.logout();
        } else {
          logger.out.warn("not logging out due to component rejection (dialog)?");
        }
      },
      (error) => {
        logger.out.warn("not logging out due to component error (dialog)?");
      },
    );
  }

  /**********************************/
  /* PRIVATE Methods */

  private hasSystemRole(): void {
    this.systemConfigService.hasRole(Roles.ADMIN_PANIC).then((value: boolean) => {
      logger.out.info(`BfxUserMenuComponent: ADMIN_PANIC ${value}`);
      if (value === false) {
        this.hasAdminPanicRole = false;
      } else {
        this.hasAdminPanicRole = true;
      }
    });
  }

  private initialiseHasAdminPublicProfiles(): void {
    this.systemConfigService.hasRole(Roles.ROLE_ADMIN_PUBLIC_PROFILES).then((value: boolean) => {
      logger.out.info(`BfxUserMenuComponent: ADMIN_PANIC ${value}`);
      if (value === false) {
        this.hasAdminPublicProfiles = false;
      } else {
        this.hasAdminPublicProfiles = true;
      }
    });
  }

  protected constructMenu(): Array<any> {
    const menuItems: Array<any> = [
      {
        label: "Settings",
        icon: "bfx-icon-sm icon-settings",
        command: (event) => this.settings(event.originalEvent),
        show: true,
      },
      {
        label: "Help",
        icon: "bfx-icon-sm icon-info",
        command: (event) => this.help(),
        show: true,
      },
      {
        label: "Panic",
        icon: "bfx-icon-sm icon-warning",
        command: (event) => this.panicControls(event.originalEvent),
        show: this.hasAdminPanicRole,
      },
      {
        label: "Save Profile",
        icon: "bfx-icon-sm icon-save",
        command: (event) => this.saveUserProfile(),
        show: true,
      },
      {
        label: "Save Public Profile",
        icon: "bfx-icon-sm icon-save",
        command: (event) => this.saveUserProfileAsPublic(),
        show: this.hasAdminPublicProfiles,
      },
      {
        label: "Switch Profile",
        icon: "bfx-icon-sm icon-swap_horiz",
        command: () => this.switchProfile(),
        show: true,
      },
      {
        label: "Logout",
        icon: "bfx-icon-sm icon-exit",
        command: () => this.requestLogout(),
        show: true,
      },
    ];
    let filteredMenuItems = menuItems.filter((setValue) => setValue.show);
    return filteredMenuItems;
  }
}
